import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { useBranding } from "@/hooks/useBranding";
import BrandLogo from "@/components/BrandLogo";
import { Lock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useSocialLinks } from "@/hooks/useSocialLinks";
import { getSocialIcon } from "@/lib/socialIcons";
import { useModuleSettings } from "@/hooks/useModuleSettings";

interface FooterPage {
  title: string;
  slug: string;
  footer_column: string;
  sort_order: number;
}

type FooterLink = { label: string; href: string; moduleKey?: string };

export default function LandingFooter() {
  const { appName, footerText } = useBranding();
  const [dynamicPages, setDynamicPages] = useState<FooterPage[]>([]);
  const { data: socialLinks = [] } = useSocialLinks();
  const { isModuleEnabled } = useModuleSettings();

  useEffect(() => {
    supabase
      .from("site_pages")
      .select("title, slug, footer_column, sort_order")
      .eq("published", true)
      .eq("show_in_footer", true)
      .order("sort_order", { ascending: true })
      .then(({ data }) => {
        if (data) setDynamicPages(data as FooterPage[]);
      });
  }, []);

  const dynamicGrouped = dynamicPages.reduce<Record<string, FooterPage[]>>((acc, page) => {
    if (!acc[page.footer_column]) acc[page.footer_column] = [];
    acc[page.footer_column].push(page);
    return acc;
  }, {});

  const staticColumns: { label: string; links: FooterLink[] }[] = [
    {
      label: "Products",
      links: [
        { label: "Spot Trading", href: "/app/spot", moduleKey: "spot_trading" },
        { label: "Futures", href: "/app/futures", moduleKey: "futures" },
        { label: "P2P Marketplace", href: "/app/p2p", moduleKey: "p2p" },
        { label: "Notes", href: "/notes", moduleKey: "landing_notes" },
      ],
    },
    {
      label: "Services",
      links: [
        { label: "Buy Crypto", href: "/app/trade/buy", moduleKey: "wallets" },
        { label: "Sell Crypto", href: "/app/trade/sell", moduleKey: "wallets" },
        { label: "Wallets", href: "/app/wallets", moduleKey: "wallets" },
        { label: "Exchange", href: "/app/exchange", moduleKey: "wallets" },
      ],
    },
    {
      label: "Support",
      links: [
        { label: "Help Center", href: "/app/support", moduleKey: "support_tickets" },
        { label: "KYC Verification", href: "/app/kyc", moduleKey: "kyc_verification" },
        { label: "Sign In", href: "/login" },
        { label: "Listing Application", href: "/listing-apply" },
        { label: "Sign Up", href: "/signup" },
      ],
    },
  ]
    .map((col) => ({
      ...col,
      links: col.links.filter((l) => !l.moduleKey || isModuleEnabled(l.moduleKey)),
    }))
    .filter((col) => col.links.length > 0);

  const dynamicColumns = Object.entries(dynamicGrouped).map(([colName, pages]) => ({
    label: colName,
    links: pages.map((p) => ({ label: p.title, href: `/page/${p.slug}` })),
  }));

  const allColumns = [...staticColumns, ...dynamicColumns];

  return (
    <footer className="border-t border-border/50 bg-background">
      <div className="max-w-7xl mx-auto px-6 py-14">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-10 mb-12">
          {/* Brand column */}
          <div className="md:col-span-2">
            <button
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              className="flex items-center gap-2 mb-3 hover:opacity-80 transition-opacity"
            >
              <BrandLogo className="h-6 max-w-[140px] object-contain" />
            </button>
            {footerText && <p className="text-sm text-muted-foreground leading-relaxed mb-4">{footerText}</p>}
            <p className="text-sm text-muted-foreground leading-relaxed mb-5">
              Trade crypto, access P2P markets, Notes and Futures from one secure ACEX account.
            </p>
            {/* Social Icons */}
            <div className="flex gap-3">
              {socialLinks.map((link) => {
                const { icon: Icon, color } = getSocialIcon(link.icon_key, link.platform);
                return (
                  <a
                    key={link.id}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={link.label || link.icon_key}
                    className={`w-9 h-9 rounded-lg ${color} border border-border/20 flex items-center justify-center hover:scale-110 transition-transform`}
                  >
                    <Icon className="h-4 w-4" />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Navigation columns */}
          {allColumns.map((col) => (
            <div key={col.label}>
              <h3 className="text-sm font-bold text-foreground uppercase tracking-wider mb-4">{col.label}</h3>
              <ul className={col.label === "Legal" ? "flex flex-wrap gap-x-4 gap-y-2" : "space-y-2"}>
                {col.links.map((link) => (
                  <li key={link.label}>
                    <Link
                      to={link.href}
                      className="text-sm text-muted-foreground hover:text-primary transition-colors"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="border-t border-border/50 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-xs text-muted-foreground">
            © {new Date().getFullYear()} {appName}. All rights reserved.
          </p>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Lock className="h-3 w-3" />
            <span>Secured with bank-grade encryption</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
